import { WORLD_GEN } from "../constants";
import { v2, type Vec2 } from "../math";
import { PLANT_LOOKS, type BlockKey, type PlantLook } from "../blocks";
import { T, isGround, openToOutside, type Grid, type TileId } from "../tiles";
import { Rng, valueNoise2 } from "./rng";
import { TREE_STYLES, treeTrunk, trunkPoint, type TreeStyle } from "./trees";
import { asteroidPulls, type AsteroidDef, type Biome } from "./types";

/** What kind of plant (see PLANTS): each has its own shape, drops and regrowth. */
export type PlantKind = "tree" | "reeds" | "shrub" | "glowFungus" | "frostFlower" | "roots" | "vine" | "thistle" | "fern" | "frostGrass" | "cinderBloom" | "lichenFingers" | "glowDrops";

/** Each kind's tile (and void crystals'), by its name in the block registry: there's one in each look (see PlantLook). */
const TILE_OF = {
  reeds: "REEDS",
  shrub: "SHRUB",
  glowFungus: "GLOW_FUNGUS",
  frostFlower: "FROST_FLOWER",
  roots: "ROOTS",
  vine: "HANGING_VINE",
  thistle: "THISTLE",
  fern: "FERN",
  frostGrass: "FROST_GRASS",
  cinderBloom: "CINDER_BLOOM",
  lichenFingers: "LICHEN_FINGERS",
  glowDrops: "GLOW_DROPS",
  crystals: "CRYSTAL",
} as const;

/** The look a rock's plants grow in: one for the whole rock, from its seed. */
export const plantLook = (def: AsteroidDef): PlantLook => PLANT_LOOKS[(def.seed >>> 4) % PLANT_LOOKS.length]!;

/** The tile a plant of `kind` (or void crystal) is grown of, in `look`. */
export const plantTile = (kind: keyof typeof TILE_OF, look: PlantLook): TileId => T[`${look.toUpperCase()}_${TILE_OF[kind]}` as BlockKey];

export type { TreeStyle, TreeTrunk } from "./trees";
export { treeCrown, treeTrunk, trunkPoint } from "./trees";


/**
 * A plant on an asteroid: felled or harvested whole (see PLANTS), not dug
 * tile by tile. Its tiles are fixed by generation, so only whether it's
 * standing is ever kept.
 */
export interface Plant {
  kind: PlantKind;
  /** Its tiles' indices in the asteroid's grid, and the tile each one grew as. */
  tiles: number[];
  grown: TileId[];
  /** Index of the tile it grows from (the bottom of its trunk). */
  base: number;
  /**
   * A tree: drawn as one sprite standing at (x, y) in grid coordinates,
   * heading `out` (radians), about `blocks` tall, from `seed`. Its tiles
   * are only its trunk (TREE), there to be swung at.
   */
  tree?: { style: TreeStyle; seed: number; blocks: number; x: number; y: number; out: number };
}

/** Plants, grass and glow bulbs are drawn this many tiles to the old one-tile size. */
const PLANT_SCALE = 2;

/** Which plants grow on the surface in each biome, and how likely each is (the shares add to 1). Spacing: how much further apart than trees. */
type Grows = PlantKind | TreeStyle | "crystals";
const SURFACE: Partial<Record<Biome, { plants: [Grows, number][]; spacing: number }>> = {
  meadow: { plants: [["oak", 0.2], ["birch", 0.12], ["candelabra", 0.07], ["braid", 0.08], ["thorn", 0.07], ["palm", 0.08], ["acacia", 0.08], ["shrub", 0.2], ["thistle", 0.1]], spacing: 1 },
  grove: { plants: [["willow", 0.12], ["oak", 0.1], ["pagoda", 0.05], ["stilt", 0.06], ["bell", 0.05], ["kelp", 0.04], ["palm", 0.03], ["cypress", 0.05], ["reeds", 0.3], ["shrub", 0.08], ["fern", 0.12]], spacing: 0.8 },
  twilight: { plants: [["dusk", 0.2], ["halo", 0.08], ["starbark", 0.08], ["capTree", 0.07], ["antler", 0.07], ["glowFungus", 0.5]], spacing: 1.2 },
  lichen: { plants: [["pine", 0.12], ["pagoda", 0.05], ["thorn", 0.05], ["acacia", 0.04], ["baobab", 0.04], ["shrub", 0.4], ["lichenFingers", 0.3]], spacing: 2 },
  frost: { plants: [["frost", 0.35], ["pine", 0.15], ["frostFlower", 0.3], ["shrub", 0.05], ["frostGrass", 0.15]], spacing: 1.5 },
  wreck: { plants: [["lantern", 0.15], ["bell", 0.1], ["shrub", 0.75]], spacing: 1.4 },
  scorched: { plants: [["ember", 0.4], ["cinderBloom", 0.6]], spacing: 2.5 },
};

/** Home: everything the builders planted. */
const HOME: [Grows, number][] = [["oak", 0.16], ["lantern", 0.14], ["birch", 0.1], ["pine", 0.08], ["candelabra", 0.07], ["cloud", 0.07], ["starbark", 0.09], ["cypress", 0.07], ["crystals", 0.22]];

/** Glow fungi in caves, by biome: how thick they grow (0 none). */
const CAVE_GLOW: Partial<Record<Biome, number>> = { dark: 1, twilight: 0.6, lichen: 0.5, frost: 0.4, wreck: 0.3, meadow: 0.15, grove: 0.15 };
/** Vines hanging from cave roofs, by biome. */
const CAVE_VINES: Partial<Record<Biome, number>> = { grove: 1, meadow: 0.6, twilight: 0.5, wreck: 1 };
/** Glow drops strung from cave roofs where it's darkest, by biome. */
const CAVE_DROPS: Partial<Record<Biome, number>> = { dark: 1, twilight: 0.6 };

/**
 * What paints plants into a grid: each kind's shape (see PlantKind), grown
 * out from `base` heading `out`, only into empty tiles, `size` times the
 * full size. Every plant grown is in `plants` (void crystals aren't plants).
 */
function painter(g: Grid, def: AsteroidDef, rng: Rng, size: number): { plants: Plant[]; put: (kind: Grows, base: Vec2, out: number) => void } {
  const plants: Plant[] = [];
  /** Every plant on a rock grows in its look. */
  const look = plantLook(def);
  const tile = (kind: keyof typeof TILE_OF) => plantTile(kind, look);
  /** The plant being grown, if any: plant() adds its tiles to it. */
  let growing: Plant | null = null;
  /** Plant tiles only grow into empty space, never into rock or each other. */
  const plant = (x: number, y: number, t: TileId) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    if (xi < 0 || yi < 0 || xi >= g.w || yi >= g.h || g.tiles[yi * g.w + xi] !== T.EMPTY) return;
    g.tiles[yi * g.w + xi] = t;
    growing?.tiles.push(yi * g.w + xi);
    growing?.grown.push(t);
  };
  /** Grow one plant: everything `draw` plants belongs to it. */
  const grow = (kind: PlantKind, base: Vec2, draw: () => void) => {
    growing = { kind, tiles: [], grown: [], base: Math.floor(base.y) * g.w + Math.floor(base.x) };
    draw();
    if (growing.tiles.length) plants.push(growing);
    growing = null;
  };

  /** Paint a curving line `width` tiles thick from `from`, heading `dir` radians, bending `bend` radians over its length. */
  const stroke = (from: Vec2, dir: number, len: number, bend: number, t: TileId, width = 1): Vec2 => {
    let p = from;
    for (let s = 0; s < len; s += 0.3) {
      const a = dir + bend * (s / len) ** 2;
      p = v2(p.x + Math.cos(a) * 0.3, p.y + Math.sin(a) * 0.3);
      const nx = -Math.sin(a);
      const ny = Math.cos(a);
      for (let o = -(width - 1) / 2; o <= (width - 1) / 2 + 1e-9; o += 0.5) plant(p.x + nx * o, p.y + ny * o, t);
    }
    return p;
  };
  const blob = (c: Vec2, r: number, t: TileId, holes = 0) => {
    for (let dy = -r; dy <= r; dy += 0.5) for (let dx = -r; dx <= r; dx += 0.5) if (dx * dx + dy * dy <= r * r && (!holes || valueNoise2(def.seed + 9, (c.x + dx) * 0.7, (c.y + dy) * 0.7) > holes)) plant(c.x + dx, c.y + dy, t);
  };

  /** The style of the tree being grown (put sets it). */
  let style: TreeStyle = "oak";
  const shapes: Record<PlantKind | "crystals", (base: Vec2, out: number) => void> = {
    /** A tree: a column of trunk tiles to swing at, under the sprite the client draws. */
    tree: (base, out) => {
      const blocks = Math.round(rng.range(36, 50) * Math.max(0.5, size) * (style === "pine" ? 1.1 : style === "birch" ? 0.95 : 1));
      const seed = rng.int(1, 0x7fffffff);
      growing!.tree = { style, seed, blocks, x: base.x, y: base.y, out };
      // The trunk, along its curve (as it's drawn) and as thick, up to where its branches start: tiles to swing at.
      const trunk = treeTrunk(style, seed, blocks);
      const up = v2(Math.cos(out), Math.sin(out));
      const across = v2(Math.sin(out), -Math.cos(out));
      const reach = style === "pine" ? 0.5 : 0.85;
      for (let t = 0; t <= reach; t += 0.25 / (trunk.pts[3].y || 1)) {
        const p = trunkPoint(trunk, t);
        const half = trunk.half * (1 - t * 0.45);
        for (let o = -half; o <= half; o += 0.5) plant(base.x + across.x * (p.x + o) + up.x * p.y, base.y + across.y * (p.x + o) + up.y * p.y, T.TREE);
      }
    },
    /** Reeds: a dense clump of thin stalks. */
    reeds: (base, out) => {
      const n = rng.int(4, 7);
      for (let i = 0; i < n; i++) {
        const o = (i - n / 2) * 0.8;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out + rng.range(-0.2, 0.2), rng.int(3, 6), rng.range(-0.3, 0.3), tile("reeds"), 1);
      }
    },
    /** Shrub: a low round bush. */
    shrub: (base, out) => blob(v2(base.x + Math.cos(out) * 1.5, base.y + Math.sin(out) * 1.5), rng.range(1.5, 2.5), tile("shrub")),
    /** Glow fungi: a few glowing caps on short stalks. */
    glowFungus: (base, out) => {
      const n = rng.int(1, 3);
      for (let i = 0; i < n; i++) {
        const o = (i - (n - 1) / 2) * 1.2;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out, rng.int(1, 2), 0, tile("glowFungus"), 1);
      }
    },
    /** Frost flower: a short stalk of ice petals. */
    frostFlower: (base, out) => void stroke(base, out, rng.int(2, 3), rng.range(-0.3, 0.3), tile("frostFlower"), 1),
    roots: (base, out) => void stroke(base, out, rng.int(2, 4), rng.range(-0.4, 0.4), tile("roots"), 1),
    vine: (base, out) => void stroke(base, out, rng.int(3, 8), rng.range(-0.15, 0.15), tile("vine"), 1),
    /** Sky thistle: one or two spined stalks, a purple head on each. */
    thistle: (base, out) => {
      const n = rng.int(1, 2);
      for (let i = 0; i < n; i++) {
        const o = (i - (n - 1) / 2) * 2;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out + rng.range(-0.15, 0.15), rng.int(2, 3), rng.range(-0.2, 0.2), tile("thistle"), 1);
      }
    },
    /** Fern: a mound of fronds, laid like a shrub. */
    fern: (base, out) => blob(v2(base.x + Math.cos(out) * 1.5, base.y + Math.sin(out) * 1.5), rng.range(1.5, 2.5), tile("fern")),
    /** Frost grass: a clump of brittle pale blades. */
    frostGrass: (base, out) => {
      const n = rng.int(3, 5);
      for (let i = 0; i < n; i++) {
        const o = (i - n / 2) * 0.8;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out + rng.range(-0.25, 0.25), rng.int(2, 3), rng.range(-0.3, 0.3), tile("frostGrass"), 1);
      }
    },
    /** Cinder blooms: one or two flowers glowing like embers. */
    cinderBloom: (base, out) => {
      const n = rng.int(1, 2);
      for (let i = 0; i < n; i++) {
        const o = (i - (n - 1) / 2) * 1.2;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out, rng.int(1, 2), 0, tile("cinderBloom"), 1);
      }
    },
    /** Lichen fingers: a few branching fingers tipped red, laid like glow fungi. */
    lichenFingers: (base, out) => {
      const n = rng.int(1, 3);
      for (let i = 0; i < n; i++) {
        const o = (i - (n - 1) / 2) * 1.2;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out, rng.int(1, 2), 0, tile("lichenFingers"), 1);
      }
    },
    /** Glow drops: a few threads of glowing beads hanging from a cave roof. */
    glowDrops: (base, out) => {
      const n = rng.int(1, 3);
      for (let i = 0; i < n; i++) {
        const o = (i - (n - 1) / 2) * 1;
        stroke(v2(base.x - Math.sin(out) * o, base.y + Math.cos(out) * o), out, rng.int(2, 4), rng.range(-0.1, 0.1), tile("glowDrops"), 1);
      }
    },
    /** Void crystals: a few glassy shards fanning out of the ground (not a plant: dug like rock). */
    crystals: (base, out) => {
      const n = rng.int(3, 5);
      for (let i = 0; i < n; i++) {
        const lean = rng.range(-0.6, 0.6);
        const len = i === 0 ? rng.int(5, 8) : rng.int(2, 5);
        stroke(base, out + lean, len * PLANT_SCALE, 0, tile("crystals"), (len > 4 ? 2 : 1) * PLANT_SCALE);
      }
    },
  };
  const put = (kind: Grows, base: Vec2, out: number) => {
    if (kind === "crystals") shapes.crystals(base, out);
    else if (TREE_STYLES.includes(kind)) {
      style = kind as TreeStyle;
      grow("tree", base, () => shapes.tree(base, out));
    } else grow(kind as PlantKind, base, () => shapes[kind as PlantKind](base, out));
  };
  return { plants, put };
}

/**
 * Plants growing straight out from the surface wherever it's smooth enough,
 * so they follow the curve of the asteroid like its gravity does, and into
 * its caves: what grows and how thickly from its biome and light (see
 * lifeasteroids.md, "Flora"). Home is covered, with tall trees and void
 * crystals.
 */
export function growFlora(g: Grid, def: AsteroidDef, biome: Biome): Plant[] {
  const home = def.kind === "home";
  const light = home ? 1 : (def.light ?? 0);
  const rng = new Rng(def.seed ^ 0x5eed);
  /** Plants on small rocks are smaller (they fit in the rock's padding), the more so in poor light. */
  const size = home ? 1 : 0.45 + 0.15 * light;
  const { plants, put } = painter(g, def, rng, size);

  const pick = (table: [Grows, number][]): Grows => {
    let roll = rng.next();
    for (const [k, share] of table) {
      if (roll < share) return k;
      roll -= share;
    }
    return table[table.length - 1]![0];
  };

  const out = openToOutside(g);
  const cx = g.w / 2;
  const cy = g.h / 2;
  // On a rock that pulls, up is one of the grid's four directions (see GRAVITY), so plants stand square to it; on a zero-g rock they grow straight out.
  const heading = asteroidPulls(def) ? (a: number) => Math.round(a / (Math.PI / 2)) * (Math.PI / 2) : (a: number) => a;
  const isOut = (x: number, y: number) => x < 0 || y < 0 || x >= g.w || y >= g.h || out[y * g.w + x] === 1;

  // The surface: ground tiles open to the outside, walked round by angle, planted every so often where it's smooth (further apart, the less light).
  const surface = home ? { plants: HOME, spacing: 1 } : SURFACE[biome];
  const surfaceBases: Vec2[] = [];
  if (surface && light >= WORLD_GEN.life.grass) {
    // In poor light only the hardy trees grow, and only on the cold rocks they're hardy to.
    const lit = light >= WORLD_GEN.life.trees;
    const hardy = biome === "lichen" || biome === "frost";
    const spots: { a: number; x: number; y: number; n: Vec2 }[] = [];
    for (let y = 1; y < g.h - 1; y++) {
      for (let x = 1; x < g.w - 1; x++) {
        const t = g.tiles[y * g.w + x]!;
        if (!isGround(t) || !(isOut(x + 1, y) || isOut(x - 1, y) || isOut(x, y + 1) || isOut(x, y - 1))) continue;
        // Which way is out: from the open tiles round it. Smooth: about half of them open, and facing away from the middle.
        let nx = 0;
        let ny = 0;
        let open = 0;
        for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) if (isOut(x + dx, y + dy)) [nx, ny, open] = [nx + dx, ny + dy, open + 1];
        const len = Math.hypot(nx, ny);
        if (!len || open < 16 || open > 30) continue;
        const n = v2(nx / len, ny / len);
        const rx = x + 0.5 - cx;
        const ry = y + 0.5 - cy;
        if ((n.x * rx + n.y * ry) / Math.hypot(rx, ry) < 0.5) continue;
        spots.push({ a: Math.atan2(ry, rx), x, y, n });
      }
    }
    spots.sort((p, q) => p.a - q.a || p.y - q.y || p.x - q.x);
    // How far round the surface (in blocks) the next plant may go.
    let next = -Infinity;
    for (const s of spots) {
      const r = Math.hypot(s.x + 0.5 - cx, s.y + 0.5 - cy);
      if (s.a * r < next) continue;
      const kind = pick(surface.plants);
      if (!lit && (kind === "crystals" || (TREE_STYLES.includes(kind) && !(hardy && (kind === "pine" || kind === "frost"))))) continue;
      // Reeds only grow where it's wet.
      const k = kind === "reeds" && (def.wet ?? 0) < 0.5 ? "shrub" : kind;
      const base = v2(s.x + 0.5 + s.n.x * 0.9, s.y + 0.5 + s.n.y * 0.9);
      const before = plants.length;
      put(k, base, heading(Math.atan2(s.n.y, s.n.x)));
      const grown = plants.length > before ? plants[plants.length - 1]!.tree : undefined;
      if (k !== "crystals") surfaceBases.push(base);
      next = s.a * r + (rng.range(9, 18) * PLANT_SCALE * size * surface.spacing) / Math.max(light, 0.1);
      // A tree's crown is wide: the next plant waits till it's clear of it.
      if (grown) next = Math.max(next, s.a * r + grown.blocks * 0.6);
    }
  }

  // Caves: open space not reached from outside. Vines hang from their roofs, glow fungi grow on their floors, roots come through under plants.
  const glow = CAVE_GLOW[biome] ?? 0;
  const vines = (CAVE_VINES[biome] ?? 0) * (home ? 1 : Math.min(1, light * 2));
  const drops = CAVE_DROPS[biome] ?? 0;
  for (let y = 1; y < g.h - 1; y++) {
    for (let x = 1; x < g.w - 1; x++) {
      const i = y * g.w + x;
      if (g.tiles[i] !== T.EMPTY || out[i]) continue;
      // Down is toward the middle (as gravity is): the step that way, and the one away.
      const dx = cx - (x + 0.5);
      const dy = cy - (y + 0.5);
      const [sx, sy] = Math.abs(dx) > Math.abs(dy) ? [Math.sign(dx), 0] : [0, Math.sign(dy)];
      const down = heading(Math.atan2(dy, dx));
      const floor = isGround(g.tiles[(y + sy) * g.w + x + sx]!);
      const roof = isGround(g.tiles[(y - sy) * g.w + x - sx]!);
      const roll = valueNoise2(def.seed + 131, x * 0.9, y * 0.9);
      if (floor && glow && roll > 1 - 0.18 * glow && valueNoise2(def.seed + 137, x * 0.13, y * 0.13) > 0.4) put("glowFungus", v2(x + 0.5, y + 0.5), down + Math.PI);
      else if (roof && vines && roll < 0.1 * vines) put("vine", v2(x + 0.5, y + 0.5), down);
      else if (roof && drops && roll > 0.5 && roll < 0.5 + 0.06 * drops) put("glowDrops", v2(x + 0.5, y + 0.5), down);
      else if (roof && surfaceBases.some((b) => Math.hypot(b.x - x, b.y - y) < 10) && roll < 0.25) put("roots", v2(x + 0.5, y + 0.5), down);
    }
  }
  return plants;
}
